// /**
//  * parcel - Edit
//  */

// React
import React, { useContext, useEffect, useState } from 'react';

// react hook form
import { useForm } from 'react-hook-form';

// UI
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles';
import { TextField, Grid, Button } from '@material-ui/core';

// Icon
import Icon from '@mdi/react';
import {
  mdiCounter,
  mdiArchiveOutline,
  mdiFormatListBulletedType,
  mdiCashMultiple,
  mdiCalendar,
} from '@mdi/js';

// Type
import { AddInventoryItemCreateParcel } from '../../../parcels/interfaces';
import { Variant } from '../../../products/interfaces';
import { Product } from '../../../products/interfaces';

// helpers'
import {
  flattenObject,
  removeEmptyValueObject,
  convertPersianDigit2English,
} from '../../../../utils/helpers';
import AuthContext from '../../../../authContext';
import List from './inventoryItemsList';
import JalaliUtils from '@date-io/jalaali';
import { DatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';

type Props = {
  addItem: (body: AddInventoryItemCreateParcel[]) => void;
  product: Product;
  variant: Variant;
  hasExpirationDate: boolean;
  count: number;
  onClose: () => void;
};

type Inputs = {
  amount: number;
  unitCost: string;
  productionDate: string;
  expirationDate: string;
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      flexWrap: 'wrap',
    },
    detail: {
      borderBottom: '1px solid #d8d8d8',
      paddingBottom: '10px',
      marginBottom: '20px',
      width: '100%',
      '& .icon, & h4.property': {
        color: '#8a8a8a',
      },
      '& .MuiDivider-root': {
        margin: '24px 0',
        color: '#d8d8d8',
      },
      '& h4': {
        margin: 0,
        padding: '0 8px',
      },
    },
    margin: {
      // margin: theme.spacing(1),
    },
    withoutLabel: {
      marginTop: theme.spacing(3),
    },
    textField: {
      // width: '25ch',
    },
    textInput: {
      // width: '25ch',
      '& input, .MuiSelect-root': {
        padding: '6px 0 12px !important',
      },

      '& .MuiInput-underline::before': {
        borderBottom: '1px solid #d8d8d8 !important',
      },
    },
    item: {
      display: 'flex',
      alignSelf: 'center',
      alignItems: 'center',
      fontWeight: 500,
      width: '100%',
      '& > *': {
        // margin: theme.spacing(0, 0, 0, 1),
      },
    },
    icon: {
      // marginRight: theme.spacing(1),
    },
    label: {
      display: 'flex',
      alignItems: 'center',
      fontSize: '14px',
      fontWeight: 400,
      color: '#8A8A8A',
      marginRight: '8px',
      marginTop: '21px',
    },
    addIcon: {
      textAlign: 'center',
      position: 'relative',
      paddingLeft: theme.spacing(8),
      // marginBottom: theme.spacing(7),
      '&::before': {
        content: '""',
        position: 'absolute',
        width: '100px',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%,-50%)',
        height: '100px',
        borderRadius: '50%',
        opacity: '0.2',
        backgroundColor: theme.palette.green.main,
      },
    },
    closeButton: {
      position: 'absolute',
      right: theme.spacing(1),
      top: theme.spacing(1),
      color: theme.palette.grey[500],
    },
  })
);

function useOnMount(handler: any) {
  return React.useEffect(handler, []);
}

function required(displayName: any) {
  return function validateRequired(value: any) {
    return value !== null || `${displayName} is required.`;
  };
}

export default function Add(props: Props) {
  const authContext = useContext(AuthContext);
  const classes = useStyles();
  const { register, handleSubmit, errors, watch, getValues, setValue } = useForm<
    Inputs
  >({
    defaultValues: {
      productionDate: null,
      expirationDate: null,
    },
  });
  const [inventoryItems, setInventoryItems] = useState<
    AddInventoryItemCreateParcel[]
  >([]);
  const [inventoryItemsCount, setInventoryItemsCount] = useState(0);
  const [finishedAddItem, setFinishedAddItem] = useState(false);

  const handleProductionDateChange = (date: any) => {
    setValue(
      'productionDate',
      convertPersianDigit2English(date.format('YYYY-MM-DD'))
    );
  };

  const handleExpirationDateChange = (date: any) => {
    setValue(
      'expirationDate',
      convertPersianDigit2English(date.format('YYYY-MM-DD'))
    );
  };

  const onSubmit = (data: any) => {
    const { amount } = data;
    setInventoryItemsCount(inventoryItemsCount + Number(amount));
    setInventoryItems([...inventoryItems, { ...data }]);
    setValue('amount', '');
    setValue('unitCost', '');
    setValue('productionDate', null);
    setValue('expirationDate', null);
  };

  const finishAddItem = () => {
    if (inventoryItemsCount >= Number(props.count)) {
      setFinishedAddItem(true);
    }
  };

  const getFinishedAddItem = () => {
    props.addItem(inventoryItems);
  };

  useEffect(() => {
    finishAddItem();
  }, [inventoryItemsCount]);

  useOnMount(() => {
    register(
      { name: 'productionDate', type: 'text', defaultValue: '' },
    );
    register(
      { name: 'expirationDate', type: 'text', defaultValue: '' },
    );
  });

  const values = getValues();

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid
          className={classes.detail}
          container
          spacing={5}
          alignItems="center"
        >
          <Grid item container alignItems="center" sm={6}>
            <Icon className="icon" path={mdiArchiveOutline} size={1} />
            <h4 className="property">نام کالا :</h4>
            <h4>{props.product.name}</h4>
          </Grid>
          <Grid item container alignItems="center" sm={6}>
            <Icon className="icon" path={mdiFormatListBulletedType} size={1} />
            <h4 className="property">نام تنوع کالا :</h4>
            <h4>{props.variant?.id}</h4>
          </Grid>
          <Grid item container alignItems="center" sm={6}>
            <Icon className="icon" path={mdiCounter} size={1} />
            <h4 className="property">مجموع تعداد کالا :</h4>
            <h4>{props.count}</h4>
          </Grid>
        </Grid>
        <Grid container spacing={4} alignItems="flex-start">
          <Grid container item xs={12} md={6} alignItems="flex-end">
            <div className={classes.item}>
              <span className={classes.label}>
                <Icon path={mdiCounter} size={1} className={classes.icon} />
              </span>

              <div style={{ width: '100%' }}>
                <TextField
                  fullWidth
                  id="amount"
                  name="amount"
                  className={classes.textInput}
                  label="تعداد کالا"
                  inputRef={register({ required: true })}
                  error={errors.amount && true}
                />
              </div>
            </div>
          </Grid>
          {props.hasExpirationDate ? (
            <>
              <Grid container item xs={12} md={6} alignItems="flex-end">
                <div className={classes.item}>
                  <span className={classes.label}>
                    <Icon
                      path={mdiCashMultiple}
                      size={1}
                      className={classes.icon}
                    />
                  </span>

                  <div style={{ width: '100%' }}>
                    <TextField
                      fullWidth
                      id="unitCost"
                      name="unitCost"
                      className={classes.textInput}
                      label="قیمت کالا(تومان)"
                      inputRef={register({ required: props.hasExpirationDate })}
                      error={errors.unitCost && true}
                    />
                  </div>
                </div>
              </Grid>
              <Grid container item xs={12} md={6} alignItems="flex-end">
                <div className={classes.item}>
                  <span className={classes.label}>
                    <Icon
                      path={mdiCalendar}
                      size={1}
                      className={classes.icon}
                    />
                  </span>

                  <div style={{ width: '100%' }}>
                    <MuiPickersUtilsProvider utils={JalaliUtils} locale="fa">
                      <DatePicker
                        id="productionDate"
                        name="productionDate"
                        label="تاریخ تولید"
                        format="jYYYY/jMM/jDD"
                        margin="normal"
                        disableToolbar
                        fullWidth
                        okLabel="تأیید"
                        cancelLabel="لغو"
                        defaultValue={null}
                        className={classes.textInput}
                        value={values.productionDate}
                        onChange={handleProductionDateChange}
                        error={errors.hasOwnProperty('productionDate')}
                        helperText={
                          errors.productionDate && errors.productionDate.message
                        }
                      />
                    </MuiPickersUtilsProvider>
                  </div>
                </div>
              </Grid>
              <Grid container item xs={12} md={6} alignItems="flex-end">
                <div className={classes.item}>
                  <span className={classes.label}>
                    <Icon
                      path={mdiCalendar}
                      size={1}
                      className={classes.icon}
                    />
                  </span>

                  <div style={{ width: '100%' }}>
                    <MuiPickersUtilsProvider utils={JalaliUtils} locale="fa">
                      <DatePicker
                        id="expirationDate"
                        name="expirationDate"
                        label="تاریخ انقضا"
                        format="jYYYY/jMM/jDD"
                        margin="normal"
                        disableToolbar
                        fullWidth
                        okLabel="تأیید"
                        cancelLabel="لغو"
                        defaultValue={null}
                        className={classes.textInput}
                        value={values.expirationDate}
                        onChange={handleExpirationDateChange}
                        error={errors.hasOwnProperty('expirationDate')}
                        helperText={
                          errors.expirationDate && errors.expirationDate.message
                        }
                      />
                    </MuiPickersUtilsProvider>
                  </div>
                </div>
              </Grid>
            </>
          ) : null}
          <>
            {inventoryItems.length ? (
              <Grid>
                <List modal={true} data={inventoryItems} />
              </Grid>
            ) : null}
          </>
          <Grid item container xs={8}>
            {finishedAddItem ? (
              <Button
                fullWidth
                type="button"
                size="large"
                data-cy="search"
                autoFocus
                variant="contained"
                color="secondary"
                onClick={getFinishedAddItem}
              >
                {'نهایی سازی اطلاعات کالا'}
              </Button>
            ) : (
              <Button
                fullWidth
                type="submit"
                size="large"
                data-cy="search"
                autoFocus
                variant="contained"
                color="secondary"
                disabled={
                  props.hasExpirationDate
                    ? Object.keys(
                      flattenObject(
                        removeEmptyValueObject({
                          ...watch(),
                        })
                      )
                    ).length !== 4
                    : Object.keys(
                      flattenObject(
                        removeEmptyValueObject({
                          ...watch(),
                        })
                      )
                    ).length !== 1
                }
              >
                {'تکمیل اطلاعات کالا'}
              </Button>
            )}
          </Grid>
          <Grid item container xs={4}>
            <Button
              fullWidth
              variant="contained"
              onClick={props.onClose}
              size="large"
              data-cy="search"
            >
              {'انصراف'}
            </Button>
          </Grid>
        </Grid>
      </form>
    </>
  );
}
